import React, { useMemo, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { ActionButton, MultipleSelectModal } from '@bringg-frontend/multiple-select-modal';
import { BringgFontIcons } from '@bringg/bringg-icons';
import { Notification } from '@bringg/react-components';

import useStore from '../../stores/helpers/use-context';
import { useSkillsValidator } from '../routes-planner/hooks/use-skills-validator';
import TaskTooltipActions from '../task-tooltip-body/task-tooltip-actions/task-tooltip-actions';
import { UpdatingSource } from '../types';

interface Props {
	selectedTasksIds: number[];
	changeTasksSelection: (selectedTasksIds: number[], source: UpdatingSource) => void;
}

const MultipleTasksSelect = ({ selectedTasksIds, changeTasksSelection }: Props) => {
	const [buttonsDisabled, setButtonsDisabled] = useState(false);

	const { translations, displayedRoutesList, routesColors, unassignTasksOnSameRoute, resequenceTasks, tasksById } =
		useStore();

	const firstSelectedTask = tasksById.get(selectedTasksIds[0]);

	const { areAllRequiredSkillsOnNewRoute } = useSkillsValidator();

	const moveTo = async (priority: number, runId: number) => {
		setButtonsDisabled(true);
		const selectedTasks = selectedTasksIds
			.map(selectedTaskId => tasksById.get(selectedTaskId))
			.sort((taskA, taskB) => taskA.priority - taskB.priority);
		const { success: canLoadTasks, missingSkills } = areAllRequiredSkillsOnNewRoute(selectedTasks, runId);

		if (!canLoadTasks) {
			Notification.error(
				`${translations.multiselectTranslations.missingSkillsError} ${missingSkills.join(', ')}`
			);
			setButtonsDisabled(false);
			return;
		}
		const isSuccess = await resequenceTasks(runId, selectedTasksIds, priority);
		if (isSuccess) {
			Notification.success(translations.multiselectTranslations.movedSuccessfully);
		}
		setButtonsDisabled(false);
	};

	const moveToStart = async () => moveTo(1, firstSelectedTask.run_id);
	const moveToEnd = async () => moveTo(20000, firstSelectedTask.run_id);

	const unassignTasks = async () => {
		setButtonsDisabled(true);

		const result = await unassignTasksOnSameRoute(selectedTasksIds);
		if (!result) {
			setButtonsDisabled(false);
			return;
		} else {
			Notification.success(translations.multiselectTranslations.unassignSuccessfully);
		}
		changeTasksSelection([], UpdatingSource.MultipleSelectModal);
	};

	const onCloseModal = () => {
		changeTasksSelection([], UpdatingSource.MultipleSelectModal);
	};

	const routeColor = routesColors.get(firstSelectedTask.run_id);

	const buttons: ActionButton[] = useMemo(() => {
		return [
			{
				icon: BringgFontIcons.ArrowLeft,
				text: translations.multiselectTranslations.moveToStart,
				onClick: moveToStart,
				className: 'multiselect-modal-action',
				dataTestId: 'multiple-select-move-to-start'
			},
			{
				icon: BringgFontIcons.ArrowRight,
				text: translations.multiselectTranslations.moveToEnd,
				onClick: moveToEnd,
				className: 'multiselect-modal-action',
				dataTestId: 'multiple-select-move-to-end'
			},
			{
				icon: BringgFontIcons.Forward,
				text: translations.multiselectTranslations.moveTo,
				onClick: () => null,
				className: 'multiselect-modal-action',
				dataTestId: 'multiple-select-move-to',
				tooltipOnClick: (
					<TaskTooltipActions
						routesList={displayedRoutesList}
						resequenceValues={{
							priority: firstSelectedTask.priority,
							runId: firstSelectedTask.run_id
						}}
						color={routesColors.get(firstSelectedTask.run_id)}
						tooltipTranslations={translations.tooltipTranslations}
						onResequenceChange={moveTo}
					/>
				)
			},
			{
				icon: BringgFontIcons.Unuser,
				text: translations.multiselectTranslations.unassign,
				onClick: unassignTasks,
				className: 'multiselect-modal-action',
				dataTestId: 'multiple-select-unassign-tasks'
			}
		];
	}, [selectedTasksIds, firstSelectedTask.run_id]);

	return (
		<MultipleSelectModal
			selectedCount={selectedTasksIds.length}
			selectedTypeName={translations.multiselectTranslations.selected}
			color={routeColor}
			actionButtons={buttons}
			buttonsDisabled={buttonsDisabled}
			onCloseModal={onCloseModal}
		/>
	);
};

export default observer(MultipleTasksSelect);
